import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

interface Props {
  agentId: string;
}

const LEVEL_COLORS: Record<string, string> = {
  error: 'bg-red-500/20 text-red-400',
  fatal: 'bg-red-600/20 text-red-300',
  warn: 'bg-yellow-500/20 text-yellow-400',
  warning: 'bg-yellow-500/20 text-yellow-400',
  info: 'bg-blue-500/20 text-blue-400',
  debug: 'bg-white/[0.06] text-white/40',
};

const AgentRemoteLogsView: React.FC<Props> = ({ agentId }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getAgentLogs(agentId, {
        level: level !== 'all' ? level : undefined,
        search: search || undefined,
        limit: 200,
      });
      setLogs(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Failed to fetch agent logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [agentId, level, search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  return (
    <div className="bg-[#0a0a14] border border-white/[0.06] rounded-xl overflow-hidden">
      {/* Terminal chrome header */}
      <div className="flex items-center gap-2 px-4 py-2 bg-white/[0.02] border-b border-white/[0.06]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        </div>
        <span className="text-[10px] text-white/30 font-mono ml-2">remote logs</span>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06] bg-white/[0.01]">
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-1.5 text-sm text-white/70 font-mono focus:outline-none focus:border-anchor-blue/40"
        >
          <option value="all">All Levels</option>
          <option value="error">Error</option>
          <option value="warn">Warning</option>
          <option value="info">Info</option>
          <option value="debug">Debug</option>
        </select>

        <form onSubmit={handleSearchSubmit} className="flex-1 flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search logs..."
            className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-1.5 text-sm text-white/70 font-mono placeholder:text-white/20 focus:outline-none focus:border-anchor-blue/40"
          />
          <button
            type="submit"
            className="px-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-md text-sm text-white/50 hover:text-white/70 hover:bg-white/[0.06] transition-colors font-mono"
          >
            Search
          </button>
        </form>

        <button
          onClick={fetchLogs}
          disabled={loading}
          className="px-3 py-1.5 bg-anchor-blue/10 border border-anchor-blue/20 rounded-md text-sm text-anchor-blue hover:bg-anchor-blue/20 transition-colors font-mono disabled:opacity-40"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Log entries */}
      <div className="max-h-[600px] overflow-y-auto">
        {loading && logs.length === 0 ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-anchor-blue mx-auto"></div>
            <p className="text-white/30 text-sm font-mono mt-3">Loading logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-white/30 text-sm font-mono">No log entries found</p>
            <p className="text-white/15 text-xs font-mono mt-1">Logs will appear here once the agent starts uploading</p>
          </div>
        ) : (
          <div className="p-2 space-y-0.5">
            {logs.map((log, i) => {
              const levelKey = (log.level || 'info').toLowerCase();
              const badgeClass = LEVEL_COLORS[levelKey] || LEVEL_COLORS.info;
              const ts = log.timestamp ? new Date(log.timestamp).toLocaleString() : '';

              return (
                <div
                  key={log.id || i}
                  className="flex items-start gap-2 py-1.5 px-2 rounded hover:bg-white/[0.02] transition-colors group"
                >
                  <span className="text-[10px] text-white/15 font-mono whitespace-nowrap pt-0.5 min-w-[140px]">
                    {ts}
                  </span>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded uppercase whitespace-nowrap ${badgeClass}`}>
                    {(log.level || 'info').slice(0, 5)}
                  </span>
                  <span className="text-[11px] font-mono text-white/60 leading-relaxed break-all">
                    {log.message}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with count */}
      {logs.length > 0 && (
        <div className="px-4 py-2 border-t border-white/[0.06] bg-white/[0.01]">
          <span className="text-[10px] text-white/20 font-mono">{logs.length} entries</span>
        </div>
      )}
    </div>
  );
};

export default AgentRemoteLogsView;
